import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useCompanyData } from '@vibesites/runtime/react';
import { CommunityCard } from './CommunityCard';
import {
  selectCommunity,
  setMapExpanded,
  setMobileBrowseView,
  setUserLocation,
  useCommunitiesBrowse,
} from './communitiesBrowseStore';
import {
  getCommunityProperties,
  getLatLng,
  type CommunityProperty,
} from './propertyUtils';

type GoogleMapsApi = {
  maps: {
    Map: new (el: HTMLElement, opts: Record<string, unknown>) => GoogleMap;
    Marker: new (opts: Record<string, unknown>) => GoogleMarker;
    InfoWindow: new (opts?: Record<string, unknown>) => GoogleInfoWindow;
    LatLngBounds: new () => GoogleLatLngBounds;
    LatLng: new (lat: number, lng: number) => unknown;
    Size: new (w: number, h: number) => unknown;
    Point: new (x: number, y: number) => unknown;
    event: {
      clearInstanceListeners: (instance: unknown) => void;
      addListener: (instance: unknown, event: string, handler: (...args: unknown[]) => void) => unknown;
      trigger: (instance: unknown, event: string) => void;
    };
  };
};

type GoogleMap = {
  fitBounds: (bounds: GoogleLatLngBounds, padding?: number) => void;
  panTo: (latLng: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  getZoom: () => number | undefined;
  setCenter: (latLng: { lat: number; lng: number }) => void;
  addListener: (event: string, handler: () => void) => void;
};

type GoogleMarker = {
  setMap: (map: GoogleMap | null) => void;
  setIcon: (icon: Record<string, unknown>) => void;
  setZIndex: (z: number) => void;
  getPosition: () => { lat: () => number; lng: () => number } | null;
  addListener: (event: string, handler: () => void) => void;
};

type GoogleInfoWindow = {
  setContent: (content: string | HTMLElement) => void;
  open: (opts: { map: GoogleMap; anchor: GoogleMarker }) => void;
  close: () => void;
  addListener: (event: string, handler: () => void) => void;
};

type GoogleLatLngBounds = {
  extend: (latLng: { lat: number; lng: number }) => void;
  getCenter: () => unknown;
  isEmpty?: () => boolean;
};

declare global {
  interface Window {
    google?: GoogleMapsApi;
  }
}

const MAPS_SCRIPT_ID = '__ala_communities_map';
const DEFAULT_API_KEY =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { GOOGLE_MAPS_API_KEY?: string } }).env?.GOOGLE_MAPS_API_KEY) ||
  '';

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f1f3f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5c6670' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cfd6dd' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#9ec9e6' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#6b8fa8' }] },
];

function pinSvg(fill: string, selected = false) {
  const scale = selected ? 1.15 : 1;
  const w = 36 * scale;
  const h = 48 * scale;
  return encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 36 48">
      <path fill="${fill}" d="M18 0C8.06 0 0 8.06 0 18c0 12.75 18 30 18 30s18-17.25 18-30C36 8.06 27.94 0 18 0z"/>
      <circle cx="18" cy="18" r="6.5" fill="#ffffff"/>
    </svg>`,
  );
}

function clusterSvg(count: number) {
  const label = count > 99 ? '99+' : String(count);
  return encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <circle cx="22" cy="22" r="20" fill="#061628"/>
      <circle cx="22" cy="22" r="16" fill="#0b2a4a"/>
      <text x="22" y="27" text-anchor="middle" fill="#ffffff" font-size="14" font-family="Arial, sans-serif" font-weight="700">${label}</text>
    </svg>`,
  );
}

let loaderPromise: Promise<void> | null = null;

function loadGoogleMapsApi(apiKey: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.google?.maps) return Promise.resolve();
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    if (document.getElementById(MAPS_SCRIPT_ID)) {
      const check = () => {
        if (window.google?.maps) resolve();
        else setTimeout(check, 40);
      };
      check();
      return;
    }

    const script = document.createElement('script');
    script.id = MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.onload = () => resolve();
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error('Google Maps API could not load.'));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

type Mappable = { property: CommunityProperty; coords: { lat: number; lng: number }; id: string };

function isLargeScreen() {
  return typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
}

function pinIcon(gmaps: GoogleMapsApi['maps'], selected: boolean) {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${pinSvg(selected ? '#d50028' : '#061628', selected)}`,
    scaledSize: new gmaps.Size(selected ? 32 : 28, selected ? 43 : 38),
    anchor: new gmaps.Point(selected ? 16 : 14, selected ? 43 : 38),
  };
}

/** Zoom bands that share a cluster grid; markers only need rebuilding when the band changes. */
function clusterTier(zoom: number) {
  return zoom >= 8 ? 3 : zoom >= 6 ? 2 : zoom >= 4 ? 1 : 0;
}

function clusterPoints(points: Mappable[], zoom: number): Array<
  | { type: 'point'; item: Mappable }
  | { type: 'cluster'; count: number; coords: { lat: number; lng: number }; items: Mappable[] }
> {
  if (zoom >= 8 || points.length <= 12) {
    return points.map((item) => ({ type: 'point' as const, item }));
  }

  const cell = zoom >= 6 ? 1.2 : zoom >= 4 ? 2.4 : 4.5;
  const buckets = new Map<string, Mappable[]>();
  for (const item of points) {
    const key = `${Math.round(item.coords.lat / cell)}_${Math.round(item.coords.lng / cell)}`;
    const list = buckets.get(key) || [];
    list.push(item);
    buckets.set(key, list);
  }

  const out: Array<
    | { type: 'point'; item: Mappable }
    | { type: 'cluster'; count: number; coords: { lat: number; lng: number }; items: Mappable[] }
  > = [];

  for (const items of buckets.values()) {
    if (items.length === 1) {
      out.push({ type: 'point', item: items[0] });
      continue;
    }
    const coords = {
      lat: items.reduce((sum, item) => sum + item.coords.lat, 0) / items.length,
      lng: items.reduce((sum, item) => sum + item.coords.lng, 0) / items.length,
    };
    out.push({ type: 'cluster', count: items.length, coords, items });
  }
  return out;
}

interface CommunitiesMapCanvasProps {
  /** Height classes for the map frame when not expanded. */
  heightClassName: string;
  errorLabel: ReactNode;
  emptyLabel: ReactNode;
  /** Compact icon-only controls for narrow split layouts. */
  compactControls?: boolean;
  /** Bottom offset for the mobile pin preview, e.g. to clear a bottom sheet. */
  mobilePreviewClassName?: string;
  className?: string;
}

export function CommunitiesMapCanvas({
  heightClassName,
  errorLabel,
  emptyLabel,
  compactControls = false,
  mobilePreviewClassName,
  className = '',
}: CommunitiesMapCanvasProps) {
  const companyData = useCompanyData();
  const browse = useCommunitiesBrowse();
  const properties = useMemo(() => getCommunityProperties(companyData), [companyData]);
  const filteredSet = useMemo(() => new Set(browse.filteredIds), [browse.filteredIds]);
  const mappable = useMemo(() => {
    const all = properties
      .map((property) => {
        const coords = getLatLng(property);
        const id = String(property.multihub_property_id || '');
        if (!coords || !id) return null;
        return { property, coords, id };
      })
      .filter((entry): entry is Mappable => Boolean(entry));

    if (!browse.resultsReady) return all;
    return all.filter((entry) => filteredSet.has(entry.id));
  }, [properties, browse.filteredIds, browse.resultsReady, filteredSet]);
  // Re-sorting the list (or "Near me" switching to distance sort) reorders ids without changing
  // which pins exist, so the map keys off the id set rather than the array.
  const mappableKey = useMemo(
    () => mappable.map((entry) => entry.id).sort().join(','),
    [mappable],
  );

  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMap | null>(null);
  const markersRef = useRef<GoogleMarker[]>([]);
  const infoRef = useRef<GoogleInfoWindow | null>(null);
  const infoRootRef = useRef<Root | null>(null);
  const markerByIdRef = useRef<Map<string, GoogleMarker>>(new Map());
  const mappableRef = useRef(mappable);
  const selectedRef = useRef(browse.selectedId);
  const styledSelectedRef = useRef<string | null>(null);
  const openInfoIdRef = useRef<string | null>(null);
  const builtTierRef = useRef<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'empty'>('loading');
  const [locating, setLocating] = useState(false);

  mappableRef.current = mappable;
  selectedRef.current = browse.selectedId;

  const closeInfo = () => {
    openInfoIdRef.current = null;
    infoRef.current?.close();
  };

  const openInfo = (property: CommunityProperty, marker: GoogleMarker) => {
    const map = mapRef.current;
    const infoWindow = infoRef.current;
    const gmaps = window.google?.maps;
    if (!map || !infoWindow || !gmaps) return;

    const container = document.createElement('div');
    infoRootRef.current?.unmount();
    const root = createRoot(container);
    infoRootRef.current = root;
    root.render(
      <CommunityCard
        property={property}
        variant="popup"
        onClose={closeInfo}
      />,
    );
    infoWindow.setContent(container);
    infoWindow.open({ map, anchor: marker });
    openInfoIdRef.current = String(property.multihub_property_id || '') || null;
  };

  const rebuildMarkers = () => {
    const map = mapRef.current;
    const gmaps = window.google?.maps;
    if (!map || !gmaps) return;

    markersRef.current.forEach((marker) => {
      gmaps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
    markersRef.current = [];
    markerByIdRef.current = new Map();

    const zoom = map.getZoom() ?? 5;
    builtTierRef.current = clusterTier(zoom);
    styledSelectedRef.current = selectedRef.current;
    const clustered = clusterPoints(mappableRef.current, zoom);

    clustered.forEach((entry) => {
      if (entry.type === 'cluster') {
        const marker = new gmaps.Marker({
          map,
          position: entry.coords,
          title: `${entry.count} communities`,
          zIndex: 10,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${clusterSvg(entry.count)}`,
            scaledSize: new gmaps.Size(44, 44),
            anchor: new gmaps.Point(22, 22),
          },
        });
        marker.addListener('click', () => {
          const bounds = new gmaps.LatLngBounds();
          entry.items.forEach((item) => bounds.extend(item.coords));
          map.fitBounds(bounds, 72);
        });
        markersRef.current.push(marker);
        return;
      }

      const { property, coords, id } = entry.item;
      const selected = selectedRef.current === id;
      const marker = new gmaps.Marker({
        map,
        position: coords,
        title: property.property_name || 'Community',
        zIndex: selected ? 100 : 20,
        icon: pinIcon(gmaps, selected),
      });

      marker.addListener('click', () => {
        if (isLargeScreen()) {
          openInfo(property, marker);
        } else {
          closeInfo();
          setMobileBrowseView('map');
        }
        selectCommunity(id, { focus: true, source: 'map' });
      });

      markersRef.current.push(marker);
      markerByIdRef.current.set(id, marker);
    });

    // Removing the anchor marker closes the info window, so re-anchor it to the replacement.
    const openId = openInfoIdRef.current;
    if (openId) {
      const marker = markerByIdRef.current.get(openId);
      const item = mappableRef.current.find((entry) => entry.id === openId);
      if (marker && item && isLargeScreen()) openInfo(item.property, marker);
      else closeInfo();
    }
  };

  const restyleSelection = () => {
    const gmaps = window.google?.maps;
    if (!gmaps) return;
    const prev = styledSelectedRef.current;
    const next = selectedRef.current;
    if (prev === next) return;
    const prevMarker = prev ? markerByIdRef.current.get(prev) : undefined;
    if (prevMarker) {
      prevMarker.setIcon(pinIcon(gmaps, false));
      prevMarker.setZIndex(20);
    }
    const nextMarker = next ? markerByIdRef.current.get(next) : undefined;
    if (nextMarker) {
      nextMarker.setIcon(pinIcon(gmaps, true));
      nextMarker.setZIndex(100);
    }
    styledSelectedRef.current = next;
  };

  useEffect(() => {
    if (!mapElRef.current) return;
    if (!DEFAULT_API_KEY) {
      setStatus('error');
      return;
    }

    let cancelled = false;

    const mount = async () => {
      try {
        await loadGoogleMapsApi(DEFAULT_API_KEY);
        if (cancelled || !mapElRef.current || !window.google?.maps) return;

        const gmaps = window.google.maps;
        const map = new gmaps.Map(mapElRef.current, {
          center: { lat: 32.8, lng: -96.8 },
          zoom: 5,
          styles: MAP_STYLES,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        mapRef.current = map;

        const infoWindow = new gmaps.InfoWindow({
          maxWidth: 340,
          pixelOffset: new gmaps.Size(0, -8),
        });
        infoRef.current = infoWindow;

        infoWindow.addListener('domready', () => {
          const closeBtn = document.querySelector('.gm-ui-hover-effect') as HTMLElement | null;
          if (closeBtn) closeBtn.style.display = 'none';
        });

        infoWindow.addListener('closeclick', () => {
          openInfoIdRef.current = null;
          infoRootRef.current?.unmount();
          infoRootRef.current = null;
        });

        map.addListener('click', () => {
          closeInfo();
          if (!isLargeScreen()) selectCommunity(null);
        });
        map.addListener('zoom_changed', () => {
          if (clusterTier(map.getZoom() ?? 5) !== builtTierRef.current) rebuildMarkers();
        });

        setStatus(mappableRef.current.length === 0 ? 'empty' : 'ready');
        rebuildMarkers();

        if (mappableRef.current.length > 0) {
          const bounds = new gmaps.LatLngBounds();
          mappableRef.current.forEach(({ coords }) => bounds.extend(coords));
          map.fitBounds(bounds, 64);
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    void mount();

    return () => {
      cancelled = true;
      infoRootRef.current?.unmount();
      infoRootRef.current = null;
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      closeInfo();
      mapRef.current = null;
    };
    // Mount once; marker updates handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    if (mappable.length === 0) {
      setStatus((prev) => (prev === 'error' ? prev : 'empty'));
      rebuildMarkers();
      return;
    }
    setStatus('ready');
    rebuildMarkers();
    // Debounced so dragging a range slider doesn't re-zoom the map on every tick.
    const timer = setTimeout(() => {
      const map = mapRef.current;
      const gmaps = window.google?.maps;
      if (!map || !gmaps) return;
      const bounds = new gmaps.LatLngBounds();
      mappableRef.current.forEach(({ coords }) => bounds.extend(coords));
      map.fitBounds(bounds, browse.mapExpanded ? 48 : 64);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappableKey, browse.mapExpanded]);

  useEffect(() => {
    restyleSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse.selectedId]);

  useEffect(() => {
    if (!browse.selectedId || !mapRef.current || browse.focusNonce <= 0) return;
    // Pin clicks already opened the popup in place; don't yank the viewport around the user.
    if (browse.focusSource === 'map') return;
    const run = () => {
      const map = mapRef.current;
      const gmaps = window.google?.maps;
      if (!map || !gmaps) return;
      gmaps.event.trigger(map, 'resize');
      const marker = markerByIdRef.current.get(browse.selectedId || '');
      const item = mappableRef.current.find((entry) => entry.id === browse.selectedId);
      if (!item) return;
      map.panTo(item.coords);
      const zoom = map.getZoom() ?? 5;
      if (zoom < 8) map.setZoom(9);
      if (isLargeScreen()) {
        if (marker) openInfo(item.property, marker);
      } else {
        closeInfo();
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse.focusNonce]);

  const sawMobileView = useRef(false);
  useEffect(() => {
    if (!sawMobileView.current) {
      sawMobileView.current = true;
      return;
    }
    const map = mapRef.current;
    const gmaps = window.google?.maps;
    if (!map || !gmaps) return;
    if (!isLargeScreen() && browse.mobileView !== 'map') return;
    requestAnimationFrame(() => {
      const current = mapRef.current;
      if (!current) return;
      gmaps.event.trigger(current, 'resize');
      if (selectedRef.current || mappableRef.current.length === 0) return;
      const bounds = new gmaps.LatLngBounds();
      mappableRef.current.forEach(({ coords }) => bounds.extend(coords));
      current.fitBounds(bounds, 64);
    });
  }, [browse.mobileView]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (!mq.matches) closeInfo();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(coords);
        mapRef.current?.panTo(coords);
        mapRef.current?.setZoom(9);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  const handleZoom = (delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    const current = map.getZoom() ?? 5;
    map.setZoom(Math.min(20, Math.max(2, current + delta)));
  };

  const selectedEntry = mappable.find((entry) => entry.id === browse.selectedId);

  return (
    <div
      className={`ala-communities-map relative w-full bg-[rgb(var(--ala-surface))] ${
        browse.mapExpanded ? 'lg:fixed lg:inset-0 lg:z-40 lg:bg-[rgb(var(--ala-surface))]' : ''
      } ${className}`}
    >
      <style>{`
        .ala-communities-map .gm-style .gm-style-iw-c {
          padding: 0 !important;
          border-radius: 12px !important;
          box-shadow: none !important;
          background: transparent !important;
          overflow: visible !important;
        }
        .ala-communities-map .gm-style .gm-style-iw-d {
          overflow: visible !important;
          max-height: none !important;
        }
        .ala-communities-map .gm-style .gm-style-iw-tc,
        .ala-communities-map .gm-style .gm-style-iw-t::after {
          display: none !important;
        }
      `}</style>

      <div
        className={`relative w-full overflow-hidden bg-[#e8eef3] ${
          browse.mapExpanded ? 'h-[100svh]' : heightClassName
        }`}
      >
        <div ref={mapElRef} className="absolute inset-0 h-full w-full" aria-label="Communities map" />

        {status === 'loading' ? (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#d9e3ec] via-[#e8eef3] to-[#cfd9e3]">
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#c5d0db]/80 to-transparent" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-body text-sm text-[rgb(var(--ala-text-muted))]">
              Loading map…
            </div>
          </div>
        ) : null}

        {status === 'error' ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#e8eef3] px-6 text-center">
            {errorLabel}
          </div>
        ) : null}

        {status === 'empty' ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#e8eef3] px-6 text-center">
            {emptyLabel}
          </div>
        ) : null}

        <div className="absolute right-3 top-3 z-10 flex flex-col gap-2 md:right-4 md:top-4">
          <div
            className="inline-flex overflow-hidden rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] shadow-sm"
            role="group"
            aria-label="Map zoom"
          >
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => handleZoom(1)}
              className="inline-flex h-11 w-11 items-center justify-center text-[rgb(var(--ala-text))] transition hover:bg-[rgb(var(--ala-surface))]"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => handleZoom(-1)}
              className="inline-flex h-11 w-11 items-center justify-center border-l border-[rgb(var(--ala-border))] text-[rgb(var(--ala-text))] transition hover:bg-[rgb(var(--ala-surface))]"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            onClick={handleLocate}
            disabled={locating}
            aria-label={compactControls ? (locating ? 'Locating' : 'Near me') : undefined}
            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] shadow-sm transition hover:border-[rgb(var(--ala-text))]/30 disabled:opacity-60 ${
              compactControls ? 'w-11 px-0' : 'px-3'
            }`}
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
            {compactControls ? null : locating ? 'Locating…' : 'Near me'}
          </button>
          <button
            type="button"
            onClick={() => setMapExpanded(!browse.mapExpanded)}
            aria-label={compactControls ? (browse.mapExpanded ? 'Exit full screen map' : 'Full screen map') : undefined}
            className={`hidden min-h-11 items-center justify-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] shadow-sm transition hover:border-[rgb(var(--ala-text))]/30 lg:inline-flex ${
              compactControls ? 'w-11 px-0' : 'px-3'
            }`}
          >
            {compactControls ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                {browse.mapExpanded ? (
                  <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            ) : browse.mapExpanded ? (
              'Exit map'
            ) : (
              'Expand map'
            )}
          </button>
        </div>

        {browse.resultCount > 0 ? (
          <div className="absolute left-3 top-3 z-10 rounded-md bg-[rgb(var(--ala-background))]/95 px-3 py-2 font-body text-xs text-[rgb(var(--ala-text))] shadow-sm lg:bottom-4 lg:left-4 lg:top-auto">
            {browse.resultCount} on map
          </div>
        ) : null}

        {selectedEntry && browse.mobileView === 'map' ? (
          <div
            className={`absolute inset-x-3 z-20 lg:hidden ${
              mobilePreviewClassName ?? (browse.compareIds.length > 0 ? 'bottom-40' : 'bottom-3')
            }`}
          >
            <CommunityCard
              property={selectedEntry.property}
              variant="popup"
              presentation="sheet"
              onClose={() => {
                closeInfo();
                selectCommunity(null);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CommunitiesMapCanvas;
